import { obterJson } from './api';
import { emailNovoValido, foneBrValido, ligarSugestaoEmail } from './criteriosSenha';
import { url, urlApi } from './config';

// Erro de campo dos formulários v2 (login, cadastro, conta): a mensagem fica embaixo do próprio campo, dentro da caixa dele e
// ligada pelo aria-describedby — <p class="campo-erro" id="…" role="alert" hidden><svg class="ico" aria-hidden="true"><use
// href="#i-info-circle"></use></svg><span></span></p> —; a caixa ganha is-err (borda vermelha) e o campo, aria-invalid. O envio
// confere tudo e foca o primeiro erro; depois, validarAoVivo. Erro sem campo (o do servidor) usa o mesmo <p>, sem campo.

// O aviso do campo é o elemento do aria-describedby com role="alert".
export function avisoDe(campo) {
    return (campo?.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
        .map(id => document.getElementById(id)).find(el => el?.getAttribute('role') === 'alert') || null;
}

function _caixaDe(campo, aviso = avisoDe(campo)) {
    return aviso?.parentElement?.contains(campo) ? aviso.parentElement : campo?.parentElement || null;
}

export function mostrarErro(campo, texto, aviso = avisoDe(campo)) {
    if (campo) {
        _caixaDe(campo, aviso)?.classList.toggle('is-err', !!texto);
        if (texto) {
            campo.setAttribute('aria-invalid', 'true');
        } else {
            campo.removeAttribute('aria-invalid');
        }
    }
    if (aviso) {
        const alvo = aviso.querySelector('span') || aviso;
        // O mesmo erro do servidor de novo (sem campo): o texto sai e volta no quadro seguinte, para o leitor de tela ler outra vez.
        if (!campo && texto && !aviso.hidden && alvo.textContent === texto) {
            alvo.textContent = '';
            requestAnimationFrame(() => { alvo.textContent = texto; });
        } else {
            alvo.textContent = texto || '';
        }
        aviso.hidden = !texto;
    }
}

// erros: [[campo, texto], ...] na ordem da tela. Mostra todos e foca o primeiro; devolve se havia algum.
export function mostrarErros(erros) {
    erros.forEach(([campo, texto]) => mostrarErro(campo, texto));
    const primeiro = erros.find(([campo, texto]) => campo && texto);
    primeiro?.[0].focus();
    return !!primeiro;
}

export function limparErros(form) {
    form.querySelectorAll('[aria-invalid="true"]').forEach(campo => mostrarErro(campo, ''));
    form.querySelectorAll('.campo-erro').forEach(aviso => mostrarErro(null, '', aviso));
}

// Em tempo real, com as regras do envio (id do campo → função que devolve o erro ou ''): ao sair do campo em que a pessoa
// mexeu e, com o erro na tela, a cada mudança (some quando corrige). "completo" confere já ao terminar de digitar (data, CPF).
// Botão de cancelar/fechar embaixo dos campos leva data-sem-conferir: o erro que entraria acima dele o empurraria entre o
// mousedown e o mouseup, e o clique se perderia.
export function validarAoVivo(form, regras, completo = {}) {
    const mexidos = new Set();
    // O Safari não dá foco ao botão clicado (o relatedTarget vem vazio): apertar o botão marca antes de o campo perder o foco
    // (no mouse, no mousedown; no toque, só depois de soltar) e a marca sai no clique ou no toque cancelado.
    let semConferir = false;
    form.addEventListener('pointerdown', e => { semConferir = !!e.target.closest('[data-sem-conferir]'); });
    // O focusin vem sempre depois do focusout que importa (e cobre soltar fora do botão ou o clique direito).
    ['click', 'pointercancel', 'focusin'].forEach(tipo => form.addEventListener(tipo, () => { semConferir = false; }));
    // Formulário que abre e fecha sem recarregar a página (reset ao abrir ou cancelar): começa de novo.
    form.addEventListener('reset', () => mexidos.clear());
    const conferir = id => mostrarErro(document.getElementById(id), regras[id]());
    form.addEventListener('input', e => {
        const id = e.target.id;
        if (!regras[id]) {
            return;
        }
        mexidos.add(id);
        if (e.target.getAttribute('aria-invalid') === 'true' || completo[id]?.(e.target.value)) {
            conferir(id);
        }
    });
    form.addEventListener('change', e => {
        if (regras[e.target.id] && e.target.type === 'checkbox' && e.target.getAttribute('aria-invalid') === 'true') {
            conferir(e.target.id);
        }
    });
    form.addEventListener('focusout', e => {
        const id = e.target.id;
        // Indo para algo da caixa do próprio campo (bandeira do telefone, lista, "mostrar senha", "Adicionar"), ainda não saiu dele.
        if (!regras[id] || !mexidos.has(id) || (e.relatedTarget && _caixaDe(e.target)?.contains(e.relatedTarget))
            || semConferir || e.relatedTarget?.closest('[data-sem-conferir]')) {
            return;
        }
        // Depois do blur do campo: a lista pode escolher sozinha ao sair.
        setTimeout(() => conferir(id), 0);
    });
}

const $ = id => document.getElementById(id);

// ---------- campos com biblioteca, máscara e lista (cadastro, concluir cadastro e dados de contato) ----------
let bibliotecas = null;
export function carregarBibliotecas() {
    bibliotecas ??= Promise.all([
        import(/* webpackChunkName: "intl-tel-input" */ 'intl-tel-input'),
        import(/* webpackChunkName: "imask" */ 'imask')
    ]).then(([{ default: intlTelInput }, { default: IMask }]) => ({ intlTelInput, IMask }))
        .catch(e => {
            bibliotecas = null;
            throw e;
        });
    return bibliotecas;
}

// Telefone com a bandeira do país e a máscara do Brasil (as regras do v1 nesta tela).
export function telefone(campo, { intlTelInput, IMask }, pais = 'br') {
    // No celular (a biblioteca decide pelo navegador, com esta mesma regra) a lista de países vai para o <body>; numa janela
    // aberta, o <body> fica inerte atrás dela e a lista não abre: vai para dentro da própria janela.
    const celular = /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const iti = intlTelInput(campo, {
        initialCountry: pais,
        preferredCountries: ['br', 'cl', 'ar', 'es', 'pt', 'us', 'fr'],
        formatOnDisplay: false,
        autoPlaceholder: 'aggressive',
        utilsScript: '/assetsV2/lib/intl-tel-util/utils.js',
        dropdownContainer: celular ? campo.closest('dialog') : null
    });
    // Número com o código colado ("+5511..."): a bandeira já mostra o país; no campo, com a máscara, o código viraria dígito.
    const codigo = `+${iti.getSelectedCountryData().dialCode || ''}`;
    if (codigo.length > 1 && campo.value.trim().startsWith(codigo)) {
        campo.value = campo.value.trim().slice(codigo.length).trim();
    }
    let mascara = null;
    const aplicar = () => {
        mascara?.destroy();
        mascara = iti.getSelectedCountryData().dialCode === '55' ? IMask(campo, { mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] }) : null;
    };
    aplicar();
    campo.addEventListener('countrychange', aplicar);
    return iti;
}

export const so = v => (v || '').replace(/\D/g, '');
export const MASCARAS = {
    data: v => so(v).slice(0, 8).replace(/^(\d{2})(\d)/, '$1/$2').replace(/^(\d{2})\/(\d{2})(\d)/, '$1/$2/$3'),
    cpf: v => so(v).slice(0, 11).replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4'),
    cep: v => so(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2'),
    mes: v => so(v).slice(0, 6).replace(/^(\d{2})(\d)/, '$1/$2')
};
// "ativa" liga e desliga a máscara (o CEP só tem máscara no Brasil; fora dele o código postal é livre).
export function mascarar(campo, tipo, ativa = () => true) {
    campo?.addEventListener('input', () => {
        if (!ativa()) {
            return;
        }
        const noFim = campo.selectionStart === campo.value.length;
        campo.value = MASCARAS[tipo](campo.value);
        if (noFim) {
            campo.setSelectionRange(campo.value.length, campo.value.length);
        }
    });
}

export const semAcento = v => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
// A API (variaveis/*) responde em camelCase e o MVC em PascalCase: lê os dois.
export const prop = (obj, nome) => obj?.[nome] ?? obj?.[nome.charAt(0).toLowerCase() + nome.slice(1)] ?? obj?.[nome.toLowerCase()];
// Só cidade de verdade ("Cidade, UF, País"): o índice também tem "Brasil, BR", o local das vagas sem cidade.
export const cidadesDaResposta = r => (Array.isArray(prop(r, 'Dados')) ? prop(r, 'Dados') : []).map(c => ({ id: prop(c, 'ID'), nome: prop(c, 'Nome') || '' })).filter(c => c.id && c.nome.split(',').length >= 3);
export const nomeDaCidade = nome => nome.replace(/, BR$/, '');

// Lista presa a um campo (combobox): busca ao digitar, setas, Enter e Esc; só a opção escolhida vale. "buscar" devolve
// [{ id, nome }]; "exato" (opcional) escolhe sozinho, ao sair do campo, a única opção igual ao que foi digitado. Com "atual"
// (o id que vale), o campo funciona como o select do currículo: o clique e as setas abrem a lista inteira (buscar('')) com
// o atual marcado.
export function listaDeOpcoes(campo, lista, { buscar, aoEscolher, vazio, minimo = 2, espera = 250, exato = null, atual = null }) {
    let timer = null;
    let controle = null;
    let itens = [];
    let ativo = -1;
    let escolhido = false;
    // Fechar também cancela a busca que ainda ia voltar (ela abriria a lista de novo).
    const fechar = () => {
        clearTimeout(timer);
        controle?.abort();
        lista.hidden = true;
        campo.setAttribute('aria-expanded', 'false');
        campo.removeAttribute('aria-activedescendant');
        ativo = -1;
    };
    const desenhar = () => {
        const opcoes = itens.map((c, i) => {
            const li = document.createElement('li');
            li.id = `${lista.id}-${i}`;
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', 'false');
            li.className = 'ent__ac-op';
            li.dataset.i = i;
            li.textContent = c.nome;
            return li;
        });
        if (!opcoes.length) {
            const nada = document.createElement('li');
            nada.className = 'ent__ac-vazio';
            nada.textContent = vazio;
            opcoes.push(nada);
        }
        lista.replaceChildren(...opcoes);
        lista.hidden = false;
        // A rolagem da lista anterior esconderia os primeiros resultados.
        lista.scrollTop = 0;
        campo.setAttribute('aria-expanded', 'true');
        campo.removeAttribute('aria-activedescendant');
        ativo = -1;
    };
    // Só a lista rola (o scrollIntoView rolaria a página junto).
    const marcar = i => {
        const lis = [...lista.children];
        ativo = i;
        lis.forEach((li, j) => li.setAttribute('aria-selected', String(j === i)));
        campo.setAttribute('aria-activedescendant', `${lista.id}-${i}`);
        const li = lis[i];
        if (li.offsetTop < lista.scrollTop) {
            lista.scrollTop = li.offsetTop;
        } else if (li.offsetTop + li.offsetHeight > lista.scrollTop + lista.clientHeight) {
            lista.scrollTop = li.offsetTop + li.offsetHeight - lista.clientHeight;
        }
    };
    const abrir = async termo => {
        controle?.abort();
        controle = new AbortController();
        try {
            itens = await buscar(termo, controle.signal);
            desenhar();
            return true;
        } catch (erro) {
            if (erro.name !== 'AbortError') {
                fechar();
            }
            return false;
        }
    };
    const abrirTudo = async () => {
        clearTimeout(timer);
        // Aberta de novo, a lista volta a valer para o blur (senão a opção marcada pelas setas se perde ao sair).
        escolhido = false;
        if (await abrir('')) {
            const i = itens.findIndex(c => c.id === atual());
            if (i >= 0) {
                marcar(i);
            }
        }
    };
    const igual = () => (exato ? itens.filter(c => exato(campo.value.trim(), c)) : []);
    const escolher = i => {
        const c = itens[i];
        if (c) {
            campo.value = c.nome;
            escolhido = true;
            aoEscolher(c);
            fechar();
        }
    };
    campo.addEventListener('input', () => {
        escolhido = false;
        aoEscolher(null);
        clearTimeout(timer);
        const termo = campo.value.trim();
        if (termo.length < minimo) {
            fechar();
            return;
        }
        timer = setTimeout(() => abrir(termo), espera);
    });
    if (atual) {
        campo.addEventListener('mousedown', e => {
            if (e.button === 0 && lista.hidden) {
                abrirTudo();
            }
        });
    }
    lista.addEventListener('mousedown', e => {
        const li = e.target.closest('[data-i]');
        if (li) {
            e.preventDefault();
            escolher(+li.dataset.i);
        }
    });
    campo.addEventListener('keydown', e => {
        // Esc com a lista aberta (mesmo a de "nenhuma cidade") fecha só a lista: numa janela, o Esc nativo fecharia a janela inteira.
        if (e.key === 'Escape' && !lista.hidden) {
            e.preventDefault();
            e.stopPropagation();
            fechar();
            return;
        }
        const seta = e.key === 'ArrowDown' || e.key === 'ArrowUp';
        if (seta && atual && lista.hidden) {
            e.preventDefault();
            abrirTudo();
            return;
        }
        if (lista.hidden || !itens.length) {
            return;
        }
        if (seta) {
            e.preventDefault();
            const desce = e.key === 'ArrowDown';
            marcar(ativo < 0 ? (desce ? 0 : itens.length - 1) : (ativo + (desce ? 1 : -1) + itens.length) % itens.length);
        } else if (e.key === 'Enter') {
            // Com a lista aberta, o Enter escolhe (o destacado, a única opção ou a igual ao digitado) e nunca envia o formulário.
            e.preventDefault();
            const iguais = igual();
            escolher(ativo >= 0 ? ativo : itens.length === 1 ? 0 : iguais.length === 1 ? itens.indexOf(iguais[0]) : -1);
        }
    });
    campo.addEventListener('blur', () => {
        const iguais = igual();
        if (!escolhido && !lista.hidden && ativo >= 0) {
            escolher(ativo);
        } else if (!escolhido && iguais.length === 1) {
            escolher(itens.indexOf(iguais[0]));
        }
        setTimeout(fechar, 150);
    });
}

// Lista de cidades (api variaveis/cidades/buscarV2, a mesma das telas de hoje).
export function listaDeCidades(campo, lista, aoEscolher, vazio) {
    listaDeOpcoes(campo, lista, {
        aoEscolher,
        vazio,
        buscar: async (termo, sinal) => cidadesDaResposta(await obterJson(urlApi(`/variaveis/cidades/buscarV2?query=${encodeURIComponent(termo)}`), { sinal }))
            .slice(0, 8).map(c => ({ id: c.id, nome: nomeDaCidade(c.nome) }))
    });
}

// ---------- telefones e endereço do cartão (concluir cadastro e dados de contato) ----------
// Telefones com bandeira. O gravado com o código de outro país já abre nele: aberto no Brasil, a máscara de lá reformataria
// os dígitos. Sem a biblioteca, o número vai como digitado, com o código do Brasil (como o v1).
export function ligarTelefones(ids) {
    const itis = {};
    const presentes = ids.filter(id => $(id));
    if (presentes.length) {
        carregarBibliotecas().then(libs => presentes.forEach(id => {
            const ddi = ($(id).dataset.ddi || '').replace('+', '');
            const paises = ddi && ddi !== '55' ? (window.intlTelInputGlobals?.getCountryData() || []).filter(c => c.dialCode === ddi) : [];
            const pais = paises.sort((a, b) => (a.priority || 0) - (b.priority || 0))[0]?.iso2;
            itis[id] = telefone($(id), libs, pais || 'br');
        })).catch(() => {});
    }
    return itis;
}

export function fone(itis, id) {
    const valor = $(id)?.value.trim() || '';
    const ddi = itis[id]?.getSelectedCountryData()?.dialCode || '55';
    const valido = !!valor && (ddi === '55' ? foneBrValido(valor) : typeof itis[id]?.isValidNumber !== 'function' || itis[id].isValidNumber());
    return { valor, ddi, valido };
}

// "Número completo" só quando faltam dígitos; completo e fora da regra, o DDD ou o número estão errados.
export const erroDoFone = (f, T) => (f.valido ? '' : f.ddi === '55' && so(f.valor).length >= 10 ? T.erroCelularInvalido : T.erroCelular);

// A busca do CEP (Home/BuscarCep) vai à base local, à BrasilAPI e ao ViaCEP e leva alguns segundos: a roda e o "Buscando o
// endereço…" na dica (e o aria-busy no campo) mostram que o servidor está trabalhando. iniciar() devolve quem desliga o
// indicador quando a busca termina (com ou sem resposta); o resultado, ou a falha, quem escreve na dica é quem chamou.
export function indicadorDeCep(campo, dica, T) {
    const caixa = campo.closest('.ent__f');
    let emCurso = 0;
    const marcar = () => {
        campo.setAttribute('aria-busy', String(emCurso > 0));
        caixa?.classList.toggle('is-buscando', emCurso > 0);
    };
    // A dica anuncia a busca e o resultado a quem usa leitor de tela.
    dica.setAttribute('aria-live', 'polite');
    return {
        iniciar() {
            const roda = document.createElement('span');
            roda.className = 'roda';
            roda.setAttribute('aria-hidden', 'true');
            dica.replaceChildren(roda, T.cepBuscando || '');
            emCurso++;
            marcar();
            let ligado = true;
            return () => {
                if (ligado) {
                    ligado = false;
                    emCurso--;
                    marcar();
                }
            };
        }
    };
}

// País, CEP (no Brasil; preenche rua e bairro) e a lista de cidades (fora do Brasil, ou quando o CEP não é achado). Os ids
// começam por p; em C ficam a cidade e o estado escolhidos, já semeados quando a pessoa tem endereço.
export function ligarEndereco(p, C, T, aoTrocarPais = () => {}) {
    const pais = $(`${p}Pais`);
    const brasil = () => pais.value === 'BR';
    const dica = $(`entH-${p}Cep`);
    const busca = indicadorDeCep($(`${p}Cep`), dica, T);

    async function buscarCep() {
        const cep = so($(`${p}Cep`).value);
        if (!brasil() || cep.length !== 8 || cep === C.cepBuscado) {
            return;
        }
        C.cepBuscado = cep;
        const parar = busca.iniciar();
        let r = null;
        let falhou = false;
        try {
            r = await obterJson(url(`/Home/BuscarCep?cep=${cep}`));
        } catch {
            falhou = true;
        }
        parar();
        // O CEP mudou (ou o país) enquanto a busca ia: a resposta é de outro endereço.
        if (C.cepBuscado !== cep) {
            return;
        }
        if (falhou || !r || r.erro || !r.cidadeID) {
            C.cidadeID = 0;
            C.estadoID = 0;
            // Falha de rede ou de tempo não é CEP inexistente: o mesmo CEP pode ser buscado de novo.
            if (falhou) {
                C.cepBuscado = '';
            }
            dica.textContent = (falhou && T.cepFalhou) || T.cepNaoEncontrado;
            $(`${p}Cidade`).value = '';
            $(`${p}CidadeF`).hidden = false;
            return;
        }
        C.cidadeID = r.cidadeID;
        C.estadoID = r.estadoID || 0;
        dica.textContent = [r.localidade, r.uf].filter(Boolean).join(', ');
        $(`${p}CidadeF`).hidden = true;
        mostrarErro($(`${p}Cep`), '');
        // Outro CEP que não o gravado: a rua e o bairro que vieram preenchidos eram do endereço antigo.
        const trocou = C.cepGravado !== undefined && cep !== so(C.cepGravado);
        if (r.bairro && (trocou || !$(`${p}Bairro`).value)) {
            $(`${p}Bairro`).value = r.bairro;
        }
        if (r.logradouro && (trocou || !$(`${p}Logradouro`).value)) {
            // O número a pessoa completa (o logradouro do CEP vem sem ele).
            $(`${p}Logradouro`).value = `${r.logradouro}, `;
        }
    }

    // Na abertura (e ao desfazer) só a visibilidade: no Brasil a cidade vem do CEP; a lista aparece sem CEP com cidade.
    function mostrar() {
        const br = brasil();
        $(`${p}CepF`).hidden = !br;
        $(`${p}CidadeF`).hidden = br && (C.cepBuscado.length === 8 || !C.cidadeID);
        aoTrocarPais(br);
    }

    mascarar($(`${p}Cep`), 'cep');
    $(`${p}Cep`).addEventListener('input', () => {
        const cep = so($(`${p}Cep`).value);
        // CEP diferente do buscado: a cidade de antes não vale mais (salvar antes da busca voltar acusa a falta dela).
        if (cep !== C.cepBuscado) {
            C.cidadeID = 0;
            C.estadoID = 0;
            C.cepBuscado = '';
            $(`entH-${p}Cep`).textContent = T.cvCepDica;
        }
        if (cep.length === 8) {
            buscarCep();
        }
    });
    listaDeCidades($(`${p}Cidade`), $(`${p}CidadeLista`), c => {
        C.cidadeID = c ? c.id : 0;
        C.estadoID = 0;
        if (c) {
            mostrarErro($(`${p}Cidade`), '');
        }
    }, T.cidadeNenhuma);
    pais.addEventListener('change', () => {
        $(`${p}Cidade`).value = '';
        C.cidadeID = 0;
        C.estadoID = 0;
        C.cepBuscado = '';
        if (C.cepGravado !== undefined) {
            C.cepGravado = '';
        }
        $(`entH-${p}Cep`).textContent = T.cvCepDica;
        mostrar();
        // De volta ao Brasil com um CEP no campo: busca de novo, já que a cidade foi zerada.
        buscarCep();
    });
    mostrar();
    // CEP gravado sem cidade (dado antigo): busca na abertura, em vez de acusar "CEP não encontrado" sem ter buscado.
    if (brasil() && !C.cidadeID) {
        buscarCep();
    }

    return {
        brasil,
        // Em tempo real: o CEP só pela forma (o "não encontrado" vem da busca, na dica, e no envio).
        regras: () => ({
            [`${p}Cep`]: () => (brasil() && so($(`${p}Cep`).value).length !== 8 ? T.cvErroCep : ''),
            [`${p}Cidade`]: () => (!$(`${p}CidadeF`).hidden && !C.cidadeID ? T.erroCidade : ''),
            [`${p}Logradouro`]: () => ($(`${p}Logradouro`).value.replace(/[\s,]+$/, '').trim() ? '' : T.erroLogradouro),
            [`${p}Bairro`]: () => ($(`${p}Bairro`).value.trim() ? '' : T.erroBairro)
        }),
        erros() {
            const e = {};
            if (brasil()) {
                e[`${p}Cep`] = so($(`${p}Cep`).value).length === 8 ? '' : T.cvErroCep;
            }
            if (!C.cidadeID && !e[`${p}Cep`]) {
                const peloCep = $(`${p}CidadeF`).hidden;
                e[peloCep ? `${p}Cep` : `${p}Cidade`] = peloCep ? T.cepNaoEncontrado : T.erroCidade;
            }
            e[`${p}Logradouro`] = $(`${p}Logradouro`).value.replace(/[\s,]+$/, '').trim() ? '' : T.erroLogradouro;
            e[`${p}Bairro`] = $(`${p}Bairro`).value.trim() ? '' : T.erroBairro;
            return e;
        },
        dados() {
            return {
                PaisID: pais.value,
                EstadoID: C.estadoID || 0,
                CidadeID: C.cidadeID,
                // Fora do Brasil não há o campo: o código postal gravado volta como estava (o UPDATE regrava o CEP).
                Cep: brasil() ? MASCARAS.cep($(`${p}Cep`).value) : (C.cepGravado || ''),
                Bairro: $(`${p}Bairro`).value.trim(),
                Logradouro: $(`${p}Logradouro`).value.replace(/[\s,]+$/, '').trim()
            };
        }
    };
}

// Países (api variaveis/paises), com o nome no idioma da página quando o navegador conhece o código: a tabela de países tem os
// nomes quase todos em inglês. Na busca, valem os dois nomes.
let paises = null;
export function carregarPaises() {
    paises ??= obterJson(urlApi('/variaveis/paises')).then(r => {
        const idioma = document.documentElement.lang || 'pt-BR';
        let nomes = null;
        try {
            nomes = new Intl.DisplayNames([idioma], { type: 'region' });
        } catch {
            nomes = null;
        }
        return (prop(r, 'Dados') || []).map(p => {
            const id = String(prop(p, 'ID') || '');
            const banco = prop(p, 'Nome') || id;
            let local = '';
            try {
                local = nomes && /^[A-Z]{2}$/.test(id) ? nomes.of(id) || '' : '';
            } catch {
                local = '';
            }
            return { id, nome: local && local !== id ? local : banco, banco };
        }).sort((a, b) => a.nome.localeCompare(b.nome, idioma));
    }).catch(erro => {
        paises = null;
        throw erro;
    });
    return paises;
}

// Os países que começam com o que foi digitado vêm antes dos que só o contêm; sem termo, todos (a lista rola).
export async function buscarPais(termo) {
    const t = semAcento(termo);
    const nomes = p => [semAcento(p.nome), semAcento(p.banco)];
    const todos = await carregarPaises();
    const comeca = todos.filter(p => nomes(p).some(n => n.startsWith(t)));
    const contem = todos.filter(p => !comeca.includes(p) && nomes(p).some(n => n.includes(t)));
    return [...comeca, ...contem];
}

// País buscável (como o do cadastro): o escolhido vai para o campo escondido, que avisa a troca (change) ao endereço. O nome
// aparece no idioma da página (o do banco é quase sempre em inglês) e, sem escolher na lista, o campo volta ao país que vale.
function ligarPais(campo, lista, oculto, T) {
    listaDeOpcoes(campo, lista, {
        buscar: buscarPais,
        vazio: T.paisNenhum,
        minimo: 0,
        espera: 0,
        atual: () => oculto.value,
        exato: (termo, p) => [semAcento(p.nome), semAcento(p.banco)].includes(semAcento(termo)),
        aoEscolher: p => {
            if (p && p.id !== oculto.value) {
                oculto.value = p.id;
                oculto.dispatchEvent(new Event('change'));
            }
        }
    });
    const mostrarAtual = () => carregarPaises().then(todos => {
        const atual = todos.find(p => p.id === oculto.value);
        if (atual && document.activeElement !== campo) {
            campo.value = atual.nome;
        }
    }).catch(() => {});
    mostrarAtual();
    campo.addEventListener('blur', mostrarAtual);
}

// ---------- dados de contato (Views/Shared/V2/_FormContatoV2: a tela de passagem e a janela das Configurações) ----------
// Celular ou telefone, como no servidor; o que estiver preenchido tem de estar certo. O e-mail segue a regra do servidor
// (Validacoes.Email). "enviar" recebe o JSON do AtualizacaoDadosSalvar de hoje e cuida do botão e da resposta.
export function ligarFormContato(form, T, volta, enviar) {
    const versao = form.dataset.versao || '0';
    // O CEP só conta como já buscado se a cidade veio junto (CEP gravado sem cidade é buscado na abertura).
    const C = { cidadeID: +form.dataset.cidade || 0, estadoID: +form.dataset.estado || 0, cepBuscado: +form.dataset.cidade ? so($('entCtCep').value) : '', cepGravado: $('entCtCep').value.trim() };
    const itis = ligarTelefones(['entCtCel', 'entCtTel', 'entCtWa']);
    const end = ligarEndereco('entCt', C, T);
    ligarPais($('entCtPaisNome'), $('entCtPaisLista'), $('entCtPais'), T);
    const erroFone = id => (fone(itis, id).valor ? erroDoFone(fone(itis, id), T) : '');
    const regras = {
        entCtEmail: () => (emailNovoValido($('entCtEmail').value.trim().toLowerCase()) ? '' : T.erroEmail),
        entCtCel: () => (fone(itis, 'entCtCel').valor ? erroFone('entCtCel') : fone(itis, 'entCtTel').valor ? '' : T.erroCelular),
        entCtTel: () => erroFone('entCtTel'),
        entCtWa: () => erroFone('entCtWa'),
        ...end.regras()
    };
    validarAoVivo(form, regras);
    ligarSugestaoEmail($('entCtEmail'), { textos: { quisDizer: T.quisDizer, usar: T.usarSugestao } });

    form.addEventListener('submit', e => {
        e.preventDefault();
        mostrarErro(null, '', $('entE-contato'));
        const erros = { ...Object.fromEntries(Object.entries(regras).map(([id, regra]) => [id, regra()])), ...end.erros() };
        if (mostrarErros(Object.entries(erros).map(([id, texto]) => [$(id), texto]))) {
            return;
        }
        const cel = fone(itis, 'entCtCel');
        const tel = fone(itis, 'entCtTel');
        const wa = fone(itis, 'entCtWa');
        enviar({
            pessoa: {
                Email: $('entCtEmail').value.trim().toLowerCase(),
                Celular: cel.valor,
                CelularPaisCode: cel.valor ? `+${cel.ddi}` : '',
                Telefone: tel.valor,
                TelefonePaisCode: tel.valor ? `+${tel.ddi}` : '',
                VersaoTermos: +versao,
                ...end.dados()
            },
            whatsapp: wa.valor ? `+${wa.ddi} ${wa.valor}` : '',
            returnUrl: volta
        });
    });
}
